import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, decodeText } from "@/lib/api";
import { REPORT_REASONS } from "@/lib/moderation";

export default function ReportMessageDialog({ open, onOpenChange, message, scope = "public" }) {
  const [reason, setReason] = useState("spam");
  const [details, setDetails] = useState("");
  const [saving, setSaving] = useState(false);
  const isPrivate = scope === "private";

  const submit = async () => {
    setSaving(true);
    try {
      const endpoint = isPrivate ? `/messages/${message.id}/report` : `/public-chat/messages/${message.id}/report`;
      await api.post(endpoint, { reason, details: details.trim() || null });
      toast.success("Report sent to the moderators. Thanks for keeping Yaprio safe.");
      onOpenChange(false);
      setDetails("");
      setReason("spam");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-md" data-testid="report-dialog">
        <DialogHeader>
          <div className="mb-1 flex h-10 w-10 items-center justify-center rounded-2xl bg-pink-100">
            <ShieldAlert className="h-5 w-5 text-pink-600" />
          </div>
          <DialogTitle className="font-heading text-lg font-extrabold">Report this message</DialogTitle>
          <DialogDescription className="text-sm">
            Reports go privately to the Yaprio moderators. {message?.username ? `@${message.username}` : "The sender"} won&apos;t be told who reported them.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3 text-sm text-slate-600">
          {message?.username && <span className="font-semibold text-slate-900">@{message.username}</span>}
          <p className="mt-1 line-clamp-3 break-words">{decodeText(message?.text)}</p>
        </div>

        <RadioGroup value={reason} onValueChange={setReason} className="gap-1">
          {REPORT_REASONS.map((r) => (
            <label key={r.value} htmlFor={`reason-${r.value}`} className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-pink-50/70">
              <RadioGroupItem value={r.value} id={`reason-${r.value}`} data-testid={`report-reason-${r.value}`} />
              <Label htmlFor={`reason-${r.value}`} className="cursor-pointer text-sm font-medium text-slate-700">{r.label}</Label>
            </label>
          ))}
        </RadioGroup>

        <Textarea value={details} onChange={(e) => setDetails(e.target.value)} maxLength={1000} placeholder="Add any details that help us review (optional)" data-testid="report-details-input" className="min-h-20 rounded-2xl border-slate-200 bg-slate-50 focus-visible:bg-white" />

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" className="rounded-full" onClick={() => onOpenChange(false)} data-testid="report-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="report-submit-btn" className="rounded-full bg-pink-500 text-white hover:bg-pink-600">
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
